/**
 * What an imported spreadsheet must carry, and what reading it gives back:
 * the rows to send, the rows the file itself refuses, the columns missing.
 */

import type { CsvTable } from "./csv";

export interface CsvColumn {
    /** The header written in the template, and the key the records are read by. */
    key: string;
    /** Other headers taken as this column, already normalised. */
    aliases: string[];
    required: boolean;
    /** An example value, for the template the administrator downloads. */
    example: string;
    /** What the column holds, when it has no i18n key: a centre's own field. */
    description?: string;
    /** Said instead of "required"/"optional" when neither is the whole truth. */
    requirement?: string;
}

/** A row the file itself refuses, before anything is sent. */
export interface CsvRowError {
    line: number;
    /** An i18n key under `center.csv.errors`. */
    code: "missing_value" | "invalid_date" | "invalid_year" | "course_mismatch" | "invalid_value" | "several_subjects";
    column?: string;
}

export interface CsvImport<T> {
    /** What is sent, each with the lines of the file it came from. */
    items: { value: T; lines: number[] }[];
    errors: CsvRowError[];
    /** Required columns the file has no header for: nothing can be read without them. */
    missingColumns: string[];
}

/** A row's value for the column, under its header or any alias; "" when empty. */
export function cell(values: Record<string, string>, column: CsvColumn): string {
    for (const key of [column.key, ...column.aliases]) {
        if (values[key] !== undefined && values[key] !== "") return values[key];
    }
    return "";
}

/** The required columns the file has no header for, by their template name. */
export function missingColumns(table: CsvTable, columns: CsvColumn[]): string[] {
    const present = new Set(table.headers);
    return columns
        .filter((column) => column.required)
        .filter((column) => ![column.key, ...column.aliases].some((key) => present.has(key)))
        .map((column) => column.key);
}
